'use client';

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
  ibow2_proto_RequestUser,
  ibow2_proto_RequestUserFavorite,
} from '@/init/swagger';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { Dispatch, SetStateAction } from 'react';
import { HospitalMap } from '../HospitalSearchPanel/HospitalMap';
import { MuiButton } from '@/components/button/MuiButton';
import { MapOutline } from 'mdi-material-ui';

type Props = {
  targetAddress: string;
  isShowMap: boolean;
  toggleIsShowMap: () => void;
  hospitalListings: HospitalListingOutput[];
  children: React.ReactNode;
  requestUser?: ibow2_proto_RequestUser;
  title: string;
  localFavorites: Array<ibow2_proto_RequestUserFavorite>;
  setLocalFavorites: Dispatch<
    SetStateAction<ibow2_proto_RequestUserFavorite[]>
  >;
  allSelected: boolean;
  toggleAllSelection: () => void;
  disabledToggleAllSelection: boolean;
  mapMarkerPortalOfficeId?: number | null;
  onMarkerClick?: (listingId: number) => void;
};

export function HospitalSearchPanelForMultiRequest({
  targetAddress,
  isShowMap,
  toggleIsShowMap,
  hospitalListings,
  children,
  requestUser,
  title,
  localFavorites,
  setLocalFavorites,
  allSelected,
  toggleAllSelection,
  disabledToggleAllSelection,
  mapMarkerPortalOfficeId,
  onMarkerClick,
}: Props) {
  return (
    <Box
      sx={{
        display: 'flex',
        px: {
          sm: 5,
          lg: 15,
        },
        pb: 10,
        gap: 3,
        '@media (max-width: 800px)': {
          px: isShowMap ? 0 : 2,
        },
      }}
    >
      <Box
        sx={{
          width: isShowMap ? '50%' : '100%',
          mt: 3,
          mb: {
            xs: 10,
            md: 3,
          },
          '@media (max-width: 800px)': {
            display: isShowMap ? 'none' : '',
            flexDirection: 'column',
            width: isShowMap ? 0 : '100%',
            height: isShowMap ? 0 : '100%',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <LocationOnOutlinedIcon sx={{ color: '#FFD600' }} />
          <Typography
            variant="JP/Body/m/Regular/Normal"
            sx={{ color: '#595960' }}
          >
            {targetAddress}
          </Typography>
        </Box>

        <Box
          sx={{
            display: isShowMap ? 'grid' : 'flex',
            gap: 1,
            mb: 3,
            '@media (max-width: 800px)': {
              display: 'grid',
            },
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              flexBasis: 'max-content',
            }}
          >
            <Typography
              variant="JP/Heading/lg/Bold/Tight"
              sx={{ color: '13131D' }}
            >
              {title || '指定した住所周辺の医療機関'}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <FormGroup
            sx={{
              backgroundColor: 'bg.lighter',
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              label={allSelected ? 'すべて選択を解除する' : 'すべて選択する'}
              style={{
                marginLeft: '0rem',
                marginRight: 2,
              }}
              control={
                <Checkbox
                  checked={allSelected}
                  disabled={disabledToggleAllSelection}
                  onChange={() => {
                    toggleAllSelection();
                  }}
                  name={'exclude_or_all_select_checkbox'}
                  sx={{ padding: '2px 4px 0 0' }}
                />
              }
            />
          </FormGroup>

          <MuiButton
            startIcon={<MapOutline />}
            onClick={toggleIsShowMap}
            variant="outlined"
            color={isShowMap ? 'primary' : 'inherit'}
            sx={{
              m: 0,
              maxHeight: 32,
            }}
          >
            Map表示
          </MuiButton>
        </Box>

        <Box
          sx={
            isShowMap
              ? {
                  width: '100%',
                  height: 800,
                  padding: 0.5,
                  overflow: 'auto',
                }
              : {
                  pb: 3,
                }
          }
        >
          {children}
        </Box>
      </Box>
      {isShowMap && (
        <Box
          sx={{
            width: isShowMap ? '50%' : '100%',
            '@media (max-width: 800px)': {
              width: isShowMap ? '100%' : 0,
              height: isShowMap ? 'calc(100dvh * 0.62)' : 0,
              marginBottom: isShowMap ? 15 : 0,
            },
          }}
        >
          {requestUser && (
            <HospitalMap
              requestUser={requestUser}
              favorites={localFavorites}
              setFavorites={setLocalFavorites}
              listings={hospitalListings}
              onToggleIsShowMap={toggleIsShowMap}
              mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
              onMarkerClick={onMarkerClick}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
